import React, { useState, useMemo } from 'react';
import { getApiBase } from '../api';

export default function AccountingIntegrations() {
  const apiBase = useMemo(() => getApiBase(), []);
  const [activeIntegration, setActiveIntegration] = useState(null);

  const integrations = [
    {
      id: 'quickbooks',
      name: 'QuickBooks Online',
      logo: '📗',
      status: 'available',
      description: 'Automatically sync invoices, bills, and payments to QuickBooks Online.',
      features: [
        'Auto-create invoices in QBO when loads are delivered',
        'Sync carrier bills as vendor bills',
        'Match payments and deposits automatically',
        'Two-way sync of customer and vendor records',
        'Real-time financial reporting'
      ],
      comingSoon: true
    },
    {
      id: 'quickbooks-desktop',
      name: 'QuickBooks Desktop',
      logo: '💼',
      status: 'available',
      description: 'Export IIF files for import into QuickBooks Desktop.',
      features: [
        'Generate IIF import files',
        'Journal entries, invoices, and bills',
        'Batch export for period-end',
        'Compatible with QB Pro, Premier, Enterprise'
      ],
      comingSoon: false,
      action: 'Use Exports tab for IIF downloads'
    },
    {
      id: 'xero',
      name: 'Xero',
      logo: '🔵',
      status: 'planned',
      description: 'Connect to Xero for automated bookkeeping.',
      features: [
        'Invoice sync',
        'Bill sync',
        'Bank reconciliation',
        'Multi-currency support'
      ],
      comingSoon: true
    },
    {
      id: 'sage',
      name: 'Sage Intacct',
      logo: '🟢',
      status: 'planned',
      description: 'Enterprise-grade integration with Sage Intacct.',
      features: [
        'Multi-entity support',
        'Dimensional reporting',
        'Advanced GL mapping',
        'Real-time sync'
      ],
      comingSoon: true
    },
    {
      id: 'sap',
      name: 'SAP Business One / S4HANA',
      logo: '🔷',
      status: 'planned',
      description: 'Export data in SAP-compatible formats for enterprise ERP.',
      features: [
        'BAPI-ready export format',
        'Material document creation',
        'FI posting compatible',
        'Batch input sessions'
      ],
      comingSoon: true
    },
    {
      id: 'netsuite',
      name: 'Oracle NetSuite',
      logo: '🔴',
      status: 'planned',
      description: 'Sync with NetSuite for mid-market and enterprise.',
      features: [
        'SuiteScript integration',
        'Custom record types',
        'Advanced revenue recognition',
        'Multi-subsidiary support'
      ],
      comingSoon: true
    }
  ];

  return (
    <div style={{ padding: 20, maxWidth: 1400, margin: '0 auto' }}>
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ margin: 0, fontSize: 24 }}>🔗 Accounting Integrations</h1>
        <p style={{ margin: '8px 0 0', color: '#6b7280' }}>
          Connect RoadOptix to your accounting software for automated bookkeeping
        </p>
      </div>

      {/* Integration Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))', gap: 20 }}>
        {integrations.map(integration => (
          <IntegrationCard
            key={integration.id}
            integration={integration}
            isActive={activeIntegration === integration.id}
            onSelect={() => setActiveIntegration(activeIntegration === integration.id ? null : integration.id)}
          />
        ))}
      </div>

      {/* QuickBooks Online Detail Modal */}
      {activeIntegration === 'quickbooks' && (
        <QuickBooksOnlineSetup onClose={() => setActiveIntegration(null)} />
      )}
    </div>
  );
}

function IntegrationCard({ integration, isActive, onSelect }) {
  const statusColors = {
    connected: { bg: '#d1fae5', text: '#065f46', label: '✓ Connected' },
    available: { bg: '#dbeafe', text: '#1e40af', label: 'Available' },
    planned: { bg: '#f3f4f6', text: '#6b7280', label: 'Coming Soon' }
  };

  const status = statusColors[integration.status] || statusColors.planned;

  return (
    <div
      style={{
        background: 'white',
        borderRadius: 12,
        border: isActive ? '2px solid #2563eb' : '1px solid #e5e7eb',
        padding: 20,
        cursor: 'pointer',
        transition: 'all 0.2s'
      }}
      onClick={onSelect}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{ fontSize: 32 }}>{integration.logo}</span>
          <div>
            <h3 style={{ margin: 0, fontSize: 18 }}>{integration.name}</h3>
            <span style={{
              display: 'inline-block',
              padding: '2px 8px',
              borderRadius: 12,
              background: status.bg,
              color: status.text,
              fontSize: 11,
              fontWeight: 600,
              marginTop: 4
            }}>
              {status.label}
            </span>
          </div>
        </div>
      </div>

      <p style={{ color: '#6b7280', fontSize: 14, margin: '0 0 16px' }}>
        {integration.description}
      </p>

      <div style={{ marginBottom: 16 }}>
        <div style={{ fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 8 }}>Features:</div>
        <ul style={{ margin: 0, paddingLeft: 20, fontSize: 13, color: '#6b7280' }}>
          {integration.features.slice(0, 3).map((f, i) => (
            <li key={i} style={{ marginBottom: 4 }}>{f}</li>
          ))}
          {integration.features.length > 3 && (
            <li style={{ color: '#9ca3af' }}>+{integration.features.length - 3} more...</li>
          )}
        </ul>
      </div>

      {integration.comingSoon ? (
        <button
          style={{
            width: '100%',
            padding: '10px 16px',
            background: '#f3f4f6',
            color: '#6b7280',
            border: 'none',
            borderRadius: 8,
            fontWeight: 600,
            cursor: 'not-allowed'
          }}
        >
          {integration.status === 'available' ? '🔜 Connect (Coming Soon)' : '📋 Request Early Access'}
        </button>
      ) : (
        <button
          style={{
            width: '100%',
            padding: '10px 16px',
            background: '#2563eb',
            color: 'white',
            border: 'none',
            borderRadius: 8,
            fontWeight: 600,
            cursor: 'pointer'
          }}
        >
          {integration.action || 'Configure'}
        </button>
      )}
    </div>
  );
}

function QuickBooksOnlineSetup({ onClose }) {
  const [step, setStep] = useState(1);

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0,0,0,0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000
    }}>
      <div style={{
        background: 'white',
        borderRadius: 16,
        padding: 32,
        maxWidth: 600,
        width: '90%',
        maxHeight: '90vh',
        overflow: 'auto'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
          <h2 style={{ margin: 0 }}>📗 QuickBooks Online Integration</h2>
          <button
            onClick={onClose}
            style={{ background: 'none', border: 'none', fontSize: 24, cursor: 'pointer', color: '#6b7280' }}
          >
            ×
          </button>
        </div>

        {/* Progress Steps */}
        <div style={{ display: 'flex', marginBottom: 32 }}>
          {['Connect', 'Configure', 'Map Accounts', 'Activate'].map((label, i) => (
            <div key={i} style={{ flex: 1, textAlign: 'center' }}>
              <div style={{
                width: 32,
                height: 32,
                borderRadius: '50%',
                background: step > i ? '#059669' : step === i + 1 ? '#2563eb' : '#e5e7eb',
                color: step >= i + 1 ? 'white' : '#6b7280',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 8px',
                fontWeight: 600
              }}>
                {step > i ? '✓' : i + 1}
              </div>
              <div style={{ fontSize: 12, color: step === i + 1 ? '#2563eb' : '#6b7280' }}>{label}</div>
            </div>
          ))}
        </div>

        {/* Step Content */}
        {step === 1 && (
          <div>
            <div style={{ textAlign: 'center', padding: 24, background: '#f0fdf4', borderRadius: 12, marginBottom: 24 }}>
              <div style={{ fontSize: 48, marginBottom: 16 }}>📗</div>
              <h3 style={{ margin: '0 0 8px' }}>Connect to QuickBooks Online</h3>
              <p style={{ color: '#6b7280', margin: 0 }}>
                Authorize RoadOptix to securely access your QuickBooks account
              </p>
            </div>

            <div style={{ marginBottom: 24 }}>
              <h4 style={{ margin: '0 0 12px' }}>What we'll sync:</h4>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <SyncItem icon="📄" label="Invoices" direction="RoadOptix → QBO" />
                <SyncItem icon="💳" label="Bills" direction="RoadOptix → QBO" />
                <SyncItem icon="💵" label="Payments" direction="Both ways" />
                <SyncItem icon="👥" label="Customers" direction="Both ways" />
                <SyncItem icon="🚛" label="Vendors (Carriers)" direction="RoadOptix → QBO" />
                <SyncItem icon="📊" label="Chart of Accounts" direction="QBO → RoadOptix" />
              </div>
            </div>

            <div style={{ padding: 16, background: '#fef3c7', borderRadius: 8, marginBottom: 24 }}>
              <strong>🔒 Security Note:</strong>
              <p style={{ margin: '8px 0 0', fontSize: 13, color: '#92400e' }}>
                RoadOptix uses OAuth 2.0 to securely connect to QuickBooks. We never store your QuickBooks password.
                You can revoke access at any time from your QuickBooks account settings.
              </p>
            </div>

            <button
              onClick={() => alert('QuickBooks OAuth integration coming soon!\n\nThis will redirect to Intuit for authorization.')}
              style={{
                width: '100%',
                padding: '14px 24px',
                background: '#2ca01c',
                color: 'white',
                border: 'none',
                borderRadius: 8,
                fontWeight: 700,
                fontSize: 16,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 8
              }}
            >
              <span>Connect to QuickBooks</span>
              <span style={{ fontSize: 20 }}>→</span>
            </button>

            <p style={{ textAlign: 'center', marginTop: 16, fontSize: 12, color: '#6b7280' }}>
              🔜 Coming Soon - OAuth integration in development
            </p>
          </div>
        )}

        {/* Future steps would go here */}
      </div>
    </div>
  );
}

function SyncItem({ icon, label, direction }) {
  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      gap: 8,
      padding: 12,
      background: '#f9fafb',
      borderRadius: 8
    }}>
      <span style={{ fontSize: 20 }}>{icon}</span>
      <div>
        <div style={{ fontWeight: 600, fontSize: 14 }}>{label}</div>
        <div style={{ fontSize: 11, color: '#6b7280' }}>{direction}</div>
      </div>
    </div>
  );
}
